import { ApiResponse } from './securepay';

export interface VerifyAccountRequest {
  accountNumber: string;
  bankCode: string;
}

/**
 * Shape is **unconfirmed** against a live backend — every dev call so far has
 * come back `success: false` with `data: null` (`ACCOUNT_VALIDATION_FAILED`, or
 * `UNAUTHORIZED: no session.` when called without a token). Every field is
 * therefore optional, and consumers must go through
 * {@link toAccountNameEnquiryResult} rather than reading `data.accountName`.
 */
export interface VerifyAccountResponse {
  accountName?: string | null;
  accountNumber?: string | null;
  bankCode?: string | null;
}

/** Normalised outcome of an account-name enquiry. Never thrown, always returned. */
export type AccountNameEnquiryResult =
  | { readonly resolved: true; readonly accountName: string }
  | { readonly resolved: false; readonly message: string };

export const ACCOUNT_NAME_ENQUIRY_FALLBACK_MESSAGE =
  "We couldn't confirm this account name. Check the account number and bank, then retry.";

/** Keys the backend might use for the resolved name, ordered by likelihood. */
const ACCOUNT_NAME_KEYS = [
  'accountName',
  'AccountName',
  'account_name',
  'beneficiaryName',
  'name',
] as const;

export function extractAccountName(data: unknown): string {
  if (!data || typeof data !== 'object') return '';
  const record = data as Record<string, unknown>;
  for (const key of ACCOUNT_NAME_KEYS) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

/** Maps backend error codes to copy a merchant or admin can act on. */
export function describeAccountEnquiryError(
  message: string | null | undefined,
): string {
  const raw = message ?? '';
  if (raw.includes('ACCOUNT_VALIDATION_FAILED')) {
    return "We couldn't verify this account. Please confirm the account number and bank.";
  }
  if (raw.includes('UNAUTHORIZED')) {
    return 'Your session has expired. Please sign in again.';
  }
  if (raw.includes('BANK_UNAVAILABLE') || raw.includes('TIMEOUT')) {
    return 'The bank did not respond in time. Please retry.';
  }
  return raw.trim() || ACCOUNT_NAME_ENQUIRY_FALLBACK_MESSAGE;
}

/**
 * Collapses every response shape into one result.
 *
 * A name is only trusted when the envelope did not explicitly fail — the
 * backend answers HTTP 200 with `success: false` for an unresolvable account,
 * so the status code alone tells us nothing.
 */
export function toAccountNameEnquiryResult(
  response: ApiResponse<VerifyAccountResponse | null> | null | undefined,
): AccountNameEnquiryResult {
  const accountName = extractAccountName(response?.data);
  if (accountName && response?.success !== false) {
    return { resolved: true, accountName };
  }
  return {
    resolved: false,
    message: describeAccountEnquiryError(response?.message),
  };
}
